import { authApi } from '@mathflat/domain/Auth'
import { useCallback, useState } from 'react'

import { ERROR_CODE } from '@/constant/errorCode'
import {
  SERVICE_ENTRY_COPY,
  SERVICE_ENTRY_NOTICE,
} from '@/contentComponents/modal/ServiceEntryModals/serviceEntryCopy'
import {
  AdminRequestModal,
  ConsentModal,
  DoneModal,
  DupeModal,
} from '@/contentComponents/modal/ServiceEntryModals/ServiceEntryModals'
import { academyController } from '@/controller/academy.controller'
import { teacherController } from '@/controller/teacher.controller'
import commonRepo from '@/repository/common.repo'
import modalService from '@/service/modal.service'
import WebviewService from '@/service/webview.service'
import { getServiceCallbackUrl, redirectWithToken } from '@/utils/serviceConnectUtils'

type ServerErrorCode = string | undefined

function getErrorCode(error: unknown): ServerErrorCode {
  return (error as { code?: string } | null)?.code
}

/** 실패는 전부 모달로 수렴시킨다 — 예외가 밖으로 새면 화면 전체가 흰 화면이 된다. */
function openNotice(message: string) {
  modalService.openConfirmModal({
    content: message,
    actionButtonText: '확인',
    hasCancelButton: false,
    onClick: () => modalService.closeModal(),
  })
}

/**
 * 신청 중 상태를 들고 있어야 해서 모달을 감싼다.
 * modalService 는 ReactNode 를 받으므로 상태는 이 안에서 산다.
 */
const ConsentModalContainer = ({ onSubmit }: { onSubmit: () => Promise<void> }) => {
  const [isSubmitting, setIsSubmitting] = useState(false)

  return (
    <ConsentModal
      brand={commonRepo.appName}
      isSubmitting={isSubmitting}
      onCancel={() => modalService.closeModal()}
      onConfirm={() => {
        setIsSubmitting(true)
        void onSubmit().finally(() => setIsSubmitting(false))
      }}
    />
  )
}

/**
 * 매쓰플랫·스쿨플랫에서 영어로 넘어가는 흐름.
 *
 * 과학과 다른 점은 **새 탭을 열기 전에 이용권 검사를 한 번 부른다**는 것이다.
 * 영어의 게이트는 토큰 클레임이 아니라 빌링 이용권이라 "권한은 있는데 이용권이 없는" 상태가
 * 정상적으로 생기고, 토큰만 넘기면 이용 기간이 끝난 뒤에도 계속 들어가진다.
 *
 * 분기는 HTTP 상태가 아니라 `code` 로 한다 — 같은 400 이라도 뜻이 다르다.
 */
export const useEnglishflatConnect = () => {
  return useCallback(async () => {
    const { user, academy } = commonRepo
    if (!user || !academy) return

    const brand = commonRepo.appName
    const copy = SERVICE_ENTRY_COPY[brand]
    const openInNewTab = !WebviewService.isAppAndroid

    const openPopup = () => {
      const popup = openInNewTab ? window.open('', '_blank') : null
      if (popup) popup.opener = null
      return popup
    }

    /**
     * 검사를 통과했을 때만 새 탭으로 보낸다. 막히면 선점한 창을 닫고 안내만 띄운다.
     *
     * 전송까지 try 안에 둔다 — 암호화는 Web Crypto 를 쓰므로 비보안 컨텍스트·일부 웹뷰에서
     * 실패할 수 있고, 팝업 주소 교체도 실패할 수 있다. 밖에 두면 빈 탭만 남고 안내가 없다.
     */
    const runAccessCheck = async (popup: Window | null) => {
      try {
        await authApi.checkServiceTypeAccess('ENGLISHFLAT')

        await redirectWithToken(getServiceCallbackUrl('ENGLISHFLAT'), {
          openInNewTab,
          preOpenedPopup: popup,
          reissue: false,
        })
      } catch (error) {
        popup?.close()
        const code = getErrorCode(error)

        if (code === ERROR_CODE.SERVICE_TYPE_NOT_SUBSCRIBED) {
          modalService.openModal(
            <AdminRequestModal brand={brand} onClose={() => modalService.closeModal()} />,
            { hasCloseButton: false },
          )
          return
        }
        if (code === ERROR_CODE.ENGLISHFLAT_ENTITLEMENT_REQUIRED) {
          openNotice(copy.entitlementRequired)
          return
        }
        // code 가 없는 예외(암호화·팝업 이동 실패 포함)도 여기로 모인다
        openNotice(SERVICE_ENTRY_NOTICE.unavailable)
      }
    }

    /** 동의가 끝나면 학원·선생님 정보가 바뀐다 — 배지와 배너가 따라오도록 다시 읽는다. */
    const refreshAccount = async () => {
      try {
        const [nextUser, nextAcademy] = await Promise.all([
          teacherController.fetchUserTeacher(user.teacherId),
          academyController.fetchMyAcademy(),
        ])
        commonRepo.user = nextUser
        commonRepo.academy = nextAcademy
      } catch {
        // 재조회가 실패해도 전환 자체는 막지 않는다. 배지는 다음 렌더에서 맞춰진다.
      }
    }

    /** 완료·중복 모달의 [바로가기] — 그 클릭이 새 제스처라 여기서 빈 창을 선점한다. */
    const goToEnglishflat = () => {
      modalService.closeModal()
      void runAccessCheck(openPopup())
    }

    const submitConsent = async () => {
      try {
        await authApi.grantServiceTypeAccess({ serviceType: 'ENGLISHFLAT' })
      } catch (error) {
        const code = getErrorCode(error)
        modalService.closeModal()

        if (code === ERROR_CODE.EXIST_SERVICE_TYPE_SUBSCRIPTION) {
          // 이미 구독된 계정이라 사용자가 잘못한 게 없다 — 경고가 아니라 이동을 제안한다.
          modalService.openModal(
            <DupeModal
              brand={brand}
              onCancel={() => modalService.closeModal()}
              onConfirm={goToEnglishflat}
            />,
            { hasCloseButton: false },
          )
          return
        }
        if (code === ERROR_CODE.SERVICE_TYPE_ACCESS_REQUEST_REQUIRED) {
          modalService.openModal(
            <AdminRequestModal brand={brand} onClose={() => modalService.closeModal()} />,
            { hasCloseButton: false },
          )
          return
        }
        if (code === ERROR_CODE.SERVICE_TYPE_LOGIN_BLOCKED) {
          openNotice(copy.loginBlocked)
          return
        }
        if (code === ERROR_CODE.ENGLISHFLAT_ENTITLEMENT_GRANT_FAILED) {
          openNotice(SERVICE_ENTRY_NOTICE.grantFailed)
          return
        }
        openNotice(SERVICE_ENTRY_NOTICE.unavailable)
        return
      }

      await refreshAccount()
      modalService.closeModal()

      // 바로 넘기지 않고 완료를 알린 뒤 [바로가기] 를 받는다. 기획 흐름 도식이 그렇게 그려져 있다.
      modalService.openModal(
        <DoneModal
          brand={brand}
          onClose={() => modalService.closeModal()}
          onConfirm={goToEnglishflat}
        />,
        { hasCloseButton: false },
      )
    }

    // 구독은 학원 단위, 과목 권한은 선생님 단위다. 둘 다 있어야 동의를 건너뛴다.
    if (academy.isEnglishflatAllowed && user.isEnglishflatAllowed) {
      await runAccessCheck(openPopup())
      return
    }

    // 권한이 없는 계정은 원장이든 소속 선생님이든 같은 신청 모달을 본다. 계정 갈림은 모달을
    // 띄울 때가 아니라 [신청] 을 누른 뒤에 서버가 답한다(기획 flow) — 그래야 소속 선생님에게
    // "길이 막혔다" 가 아니라 "한 단계 남았다" 로 이어진다. 원장 판정을 여기서 미리 하면
    // 서버 판정과 갈라질 수도 있다.
    // isCloseable: false — 신청 응답 대기 중 ESC·오버레이로 닫히면 closeModal 이 당시 최상단
    // 모달을 pop 해 경합이 생긴다. 닫기는 취소 버튼으로만 한정한다(신청 중에는 그 버튼도 막힌다).
    modalService.openModal(<ConsentModalContainer onSubmit={submitConsent} />, {
      hasCloseButton: false,
      isCloseable: false,
    })
  }, [])
}
